import React, { useEffect, useMemo, useState } from 'react'
import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    NoSymbolIcon,
    PencilSquareIcon,
    PlusIcon,
    TrashIcon
} from '@heroicons/react/24/outline'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { useNavigate, useSearchParams } from 'react-router-dom'
import PageMasthead from '../../components/PageMasthead'
import {
    CookiePlatform,
    CookieStatus,
    useDeleteCookieMutation,
    useGetCookiesQuery,
    useUpdateCookieMutation
} from '../../api/client'
import { toastError, toastSuccess } from '../../components/Notification'
import DeleteCookieModal from '../../components/cookies/DeleteCookieModal'
import { cookieStatusBadge as statusBadge } from '../../components/cookies/statusBadge'

const STORAGE_KEY = 'cookieListFilters'

type PlatformFilter = CookiePlatform | 'ALL'

type Filters = {
    platform: PlatformFilter
    showDead: boolean
    page: number
    pageSize: number
}

const DEFAULT_FILTERS: Filters = {
    platform: 'ALL',
    showDead: false,
    page: 1,
    pageSize: 20
}

const CookieListPage: React.FC = () => {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const [cookieToDelete, setCookieToDelete] = useState<number | null>(null)

    // Get initial filters from URL -> localStorage -> defaults
    const getInitialFilters = (): Filters => {
        if (searchParams.has('platform')) {
            return {
                platform: (searchParams.get('platform') as PlatformFilter) || DEFAULT_FILTERS.platform,
                showDead: searchParams.get('showDead') === 'true',
                page: parseInt(searchParams.get('page') || '1'),
                pageSize: parseInt(searchParams.get('pageSize') || '20')
            }
        }

        const saved = localStorage.getItem(STORAGE_KEY)
        if (saved) {
            try {
                return JSON.parse(saved)
            } catch {
                return DEFAULT_FILTERS
            }
        }

        return DEFAULT_FILTERS
    }

    const [filters, setFilters] = useState<Filters>(getInitialFilters)

    // Fetch cookies for all platforms
    const twitterQuery = useGetCookiesQuery({ variables: { platform: CookiePlatform.Twitter } })
    const facebookQuery = useGetCookiesQuery({ variables: { platform: CookiePlatform.Facebook } })
    const instagramQuery = useGetCookiesQuery({ variables: { platform: CookiePlatform.Instagram } })

    const loading = twitterQuery.loading || facebookQuery.loading || instagramQuery.loading
    const error = twitterQuery.error || facebookQuery.error || instagramQuery.error

    // Combine all cookies, then filter by selected platform
    const allPlatformCookies = [
        ...(twitterQuery.data?.getCookies || []),
        ...(facebookQuery.data?.getCookies || []),
        ...(instagramQuery.data?.getCookies || [])
    ]

    const allCookies = filters.platform === 'ALL'
        ? allPlatformCookies
        : allPlatformCookies.filter(c => c.platform === filters.platform)

    // Check which platforms are missing active cookies (only FB and IG matter for screenshotting)
    const platformsWithoutActiveCookies = useMemo(() => {
        // Don't show warning until queries have loaded
        if (loading) return []

        const missing: string[] = []
        const fbCookies = facebookQuery.data?.getCookies || []
        const igCookies = instagramQuery.data?.getCookies || []

        if (!fbCookies.some(c => c.status === 'ACTIVE')) {
            missing.push('Facebook')
        }
        if (!igCookies.some(c => c.status === 'ACTIVE')) {
            missing.push('Instagram')
        }
        return missing
    }, [loading, facebookQuery.data, instagramQuery.data])

    const [deleteCookieMutation, { loading: deleting }] = useDeleteCookieMutation({
        refetchQueries: ['GetCookies']
    })

    const [updateCookieMutation, { loading: updating }] = useUpdateCookieMutation({
        refetchQueries: ['GetCookies']
    })

    // Sync filters to localStorage and URL
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filters))
        const params = new URLSearchParams()
        params.set('platform', filters.platform)
        params.set('showDead', String(filters.showDead))
        params.set('page', String(filters.page))
        params.set('pageSize', String(filters.pageSize))
        setSearchParams(params, { replace: true })
    }, [filters, setSearchParams])

    // Filter cookies on client side
    const filteredCookies = useMemo(() => {
        let result = allCookies

        // Filter out dead cookies if needed
        if (!filters.showDead) {
            result = result.filter(c => c.status !== 'DEAD')
        }

        return result
    }, [allCookies, filters.showDead])

    const totalPages = Math.max(1, Math.ceil(filteredCookies.length / filters.pageSize))
    const paginated = useMemo(() => {
        const start = (filters.page - 1) * filters.pageSize
        return filteredCookies.slice(start, start + filters.pageSize)
    }, [filteredCookies, filters.page, filters.pageSize])

    // Update filter helper
    const updateFilter = (update: Partial<Filters>) => {
        setFilters(prev => ({
            ...prev,
            ...update,
            // Reset to page 1 when filters change (except when changing page itself)
            page: update.page !== undefined ? update.page : 1
        }))
    }

    const confirmDelete = async () => {
        if (!cookieToDelete) return
        try {
            await deleteCookieMutation({ variables: { cookieId: cookieToDelete } })
            toastSuccess('Cookie deleted.')
            setCookieToDelete(null)
        } catch (err) {
            console.error('Failed to delete cookie:', err)
            toastError('Failed to delete cookie. Please try again.')
        }
    }

    const toggleCookieStatus = async (cookieId: number, newStatus: CookieStatus) => {
        try {
            await updateCookieMutation({
                variables: {
                    input: {
                        cookieId,
                        status: newStatus
                    }
                }
            })
            toastSuccess(`Cookie marked as ${newStatus.toLowerCase()}.`)
        } catch (err) {
            console.error('Failed to update cookie status:', err)
            toastError('Failed to update cookie status. Please try again.')
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-ink-50 flex items-center justify-center">
                <div className="text-ink-500">Loading cookies...</div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="min-h-screen bg-ink-50 flex items-center justify-center">
                <div className="text-red-600">Error loading cookies: {error.message}</div>
            </div>
        )
    }

    const renderDate = (value?: string | null) => {
        if (!value) return <span className="text-ink-400">—</span>
        try {
            const d = parseISO(value)
            return (
                <div className="text-sm text-ink-800">
                    {formatDistanceToNow(d, { addSuffix: true })}
                    <div className="text-xs text-ink-500">{d.toLocaleString()}</div>
                </div>
            )
        } catch {
            return <span className="text-ink-500">{value}</span>
        }
    }

    return (
        <div className="min-h-screen bg-ink-50">
            <PageMasthead
                title="Cookies"
                tabTitle="Cookies"
                subtitle={`${filteredCookies.length} cookie${filteredCookies.length === 1 ? '' : 's'}`}
                actions={
                    <div className="flex items-center space-x-3">
                        <label className="flex items-center space-x-2 text-sm text-ink-700">
                            <input
                                type="checkbox"
                                checked={filters.showDead}
                                onChange={(e) => updateFilter({ showDead: e.target.checked })}
                                className="rounded border-ink-300 text-arwen-600 focus:ring-arwen-500"
                            />
                            <span>Show dead</span>
                        </label>
                        <select
                            value={filters.platform}
                            onChange={(e) => updateFilter({ platform: e.target.value as PlatformFilter })}
                            className="block border border-ink-200 rounded-md shadow-sm text-sm text-ink-700 focus:ring-arwen-500 focus:border-arwen-500"
                        >
                            <option value="ALL">All platforms</option>
                            <option value={CookiePlatform.Twitter}>Twitter/X</option>
                            <option value={CookiePlatform.Facebook}>Facebook</option>
                            <option value={CookiePlatform.Instagram}>Instagram</option>
                        </select>
                        <button
                            onClick={() => navigate('/admin/cookie/new')}
                            className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-arwen-600 hover:bg-arwen-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-arwen-500"
                        >
                            <PlusIcon className="h-4 w-4 mr-2" />
                            New cookie
                        </button>
                    </div>
                }
            />

            <p className="px-6 pt-4 font-sans text-sm text-ink-500">
                Manage platform cookies for screenshotting. Highlighted rows are dead cookies.
            </p>

            {platformsWithoutActiveCookies.length > 0 && (
                <div className="mx-6 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="font-medium text-amber-800">
                            No active cookies for {platformsWithoutActiveCookies.join(' or ')}
                        </div>
                        <div className="text-sm text-amber-700 mt-1">
                            Screenshots cannot be taken for these platforms until valid cookies are added.
                        </div>
                    </div>
                </div>
            )}

            <div className="flex flex-col px-6 py-4">
                <div className="overflow-hidden rounded-lg border border-ink-200 bg-white">
                    <table className="min-w-full divide-y divide-ink-100">
                        <thead className="bg-ink-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-[10.5px] font-semibold text-ink-500 uppercase tracking-wide">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-[10.5px] font-semibold text-ink-500 uppercase tracking-wide">
                                    Platform
                                </th>
                                <th className="px-6 py-3 text-left text-[10.5px] font-semibold text-ink-500 uppercase tracking-wide">
                                    Account
                                </th>
                                <th className="px-6 py-3 text-left text-[10.5px] font-semibold text-ink-500 uppercase tracking-wide">
                                    Fail count
                                </th>
                                <th className="px-6 py-3 text-left text-[10.5px] font-semibold text-ink-500 uppercase tracking-wide">
                                    Last used
                                </th>
                                <th className="px-6 py-3 text-left text-[10.5px] font-semibold text-ink-500 uppercase tracking-wide">
                                    Notes
                                </th>
                                <th className="px-6 py-3 text-right text-[10.5px] font-semibold text-ink-500 uppercase tracking-wide">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-ink-100">
                            {paginated.map((cookie) => (
                                <tr
                                    key={cookie.id}
                                    className={
                                        cookie.status === 'DEAD' ? 'bg-red-50/60' : 'hover:bg-ink-50'
                                    }
                                >
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span
                                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge(
                                                cookie.status
                                            )}`}
                                        >
                                            {cookie.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-ink-800">
                                            {cookie.platform === CookiePlatform.Twitter ? 'Twitter/X' :
                                             cookie.platform === CookiePlatform.Facebook ? 'Facebook' :
                                             cookie.platform === CookiePlatform.Instagram ? 'Instagram' : cookie.platform}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-ink-800">
                                            {cookie.serviceUsername}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-ink-800">
                                            {cookie.failCount}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">{renderDate(cookie.lastUsedAt)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-ink-800">
                                            {cookie.notes || <span className="text-ink-400">—</span>}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                                        {cookie.status === 'ACTIVE' ? (
                                            <button
                                                onClick={() => toggleCookieStatus(cookie.id, CookieStatus.Dead)}
                                                className="inline-flex items-center"
                                                title="Mark as dead"
                                                disabled={updating}
                                            >
                                                <NoSymbolIcon className="h-5 w-5 text-ink-400 hover:text-red-600" />
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => toggleCookieStatus(cookie.id, CookieStatus.Active)}
                                                className="inline-flex items-center"
                                                title="Mark as active"
                                                disabled={updating}
                                            >
                                                <CheckCircleIcon className="h-5 w-5 text-green-500 hover:text-green-700" />
                                            </button>
                                        )}
                                        <button
                                            onClick={() => navigate(`/admin/cookie/${cookie.id}/edit`)}
                                            className="inline-flex items-center"
                                            title="Edit"
                                        >
                                            <PencilSquareIcon className="h-5 w-5 text-ink-400 hover:text-arwen-600" />
                                        </button>
                                        <button
                                            onClick={() => setCookieToDelete(cookie.id)}
                                            className="inline-flex items-center"
                                            title="Delete"
                                        >
                                            <TrashIcon className="h-5 w-5 text-ink-400 hover:text-red-600" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filteredCookies.length === 0 ? (
                        <div className="p-6 text-center text-sm text-ink-500">
                            <ExclamationTriangleIcon className="h-6 w-6 text-ink-400 mx-auto mb-2" />
                            No cookies found. Try adjusting your filters.
                        </div>
                    ) : null}
                </div>

                {filteredCookies.length > 0 && (
                    <div className="flex items-center justify-between text-sm text-ink-500 mt-4">
                        <div>
                            Showing {(filters.page - 1) * filters.pageSize + 1}-
                            {Math.min(filters.page * filters.pageSize, filteredCookies.length)} of {filteredCookies.length}
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                className="rounded-md border border-ink-200 px-3 py-1 text-ink-700 hover:bg-ink-50 disabled:opacity-50"
                                onClick={() => updateFilter({ page: Math.max(1, filters.page - 1) })}
                                disabled={filters.page === 1}
                            >
                                Previous
                            </button>
                            <span>
                                Page {filters.page} of {totalPages}
                            </span>
                            <button
                                className="rounded-md border border-ink-200 px-3 py-1 text-ink-700 hover:bg-ink-50 disabled:opacity-50"
                                onClick={() => updateFilter({ page: Math.min(totalPages, filters.page + 1) })}
                                disabled={filters.page === totalPages}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <DeleteCookieModal
                open={cookieToDelete !== null}
                onClose={() => setCookieToDelete(null)}
                onConfirm={confirmDelete}
                loading={deleting}
            />
        </div>
    )
}

export default CookieListPage
